import _ from 'lodash';
import faker from 'faker';
import localforage from 'localforage';
import gql from 'graphql-tag';
import Store from './Store';
import DraftParent from './DraftParent';
import DraftSession from './DraftSession';
import User from './User';
import Template from './Template';
import mockCardsList from '../pages/__mocks__/cardsList.json';
import mockTemplates from '../containers/__mocks__/templates.json';
import { buildModels } from '../libraries/graphql';
import config from '../config';
import session from './Session';

export default class Draft extends Store {
  store = {
    _id: null,
    title: null,
    blurb: null,
    parents: [],
    template: null,
    prompts: {},
    participants: [],
    createdBy: null,
    createdAt: null,
    sessions: [],
    image: {},
    isSeeded: null,
  }

  static schema = {
    Draft: {
      normalizeAs: 'Draft',
      primaryKey: '_id',
      fields: {
        title: String,
        blurb: String,
        // template: "Template",
        template: 'Template',
        // parents: ["DraftParent"],
        parents: ['DraftParent'],
        participants: ['User'],
        sessions: ['DraftSession'],
        createdBy: 'User',
        updatedBy: String,
        image: String,
        _id: String,
        updatedAt: Date,
        createdAt: Date,
        isSeeded: Boolean,
      },
    },
    User: {
      normalizeAs: 'User',
      primaryKey: '_id',
      fields: {
        _id: String,
        displayName: String,
        image: String,
      },
    },
    DraftSession: {
      normalizeAs: 'Session',
      primaryKey: '_id',
      fields: {
        index: Number,
        draftId: String,
        // parents: ["DraftParent"],
        // participants: ["User"],
        // sessions: ["DraftSession"],
        fields: {},
      },
    },
    DraftParent: {
      normalizeAs: 'DraftParents',
      primaryKey: 'parentId',
      fields: {
        parentId: String,
        parentType: String,
      },
    },
    Template: {
      normalizeAs: 'Template',
      primaryKey: '_id',
      fields: {
        name: String,
        slug: String,
        blurb: String,
        _id: String,
      },
    },
    CreateOneDraftPayload: {
      normalizeAs: 'CreateOneDraftPayload',
      primaryKey: 'recordId',
      fields: {
        recordId: String,
        record: 'Draft',
      },
    },
    UpdateByIdDraftPayload: {
      normalizeAs: 'UpdateByIdDraftPayload',
      primaryKey: 'recordId',
      fields: {
        recordId: String,
        record: 'Draft',
      },
    },
  }

  defaultKeys = ['_id', 'title', 'blurb', 'parents', 'template', 'prompts', 'participants', 'createdBy', 'createdAt', 'isSeeded']

  constructor(props) {
    super(props);
    return this.setup(props);
  }

  set(...args) {
    let changes = {};

    // Has an object for first value, just set the object
    if (_.isObject(args[0])) {
      changes = _.clone(args[0]);
    }

    // Has a string first value, most probably a key value pair
    if (_.isString(args[0])) {
      changes = {
        [args[0]]: args[1],
      };
    }

    // Begin casting objects to their expected types
    const transformedChanges = _.reduce(changes, (acc, val, key) => _.assign(acc, { [key]: this.castToModel(key, val) }), {});
    Store.prototype.set.call(this, transformedChanges);
    return this;
  }

  castToModel(key, val) {
    switch (key) {
      case 'parents':
        return val.map(v => new DraftParent(v));
      case 'template':
        return new Template(val);
      case 'sessions':
        return val.map(v => new DraftSession(v));
      case 'participants':
        return val.map(v => (_.isString(v) ? null : new User(v)));
      default:
        return val;
    }
  }

  clone() {
    const vals = _.pick(this.toObject(), ['title', 'blurb', 'parents', 'template', 'prompts', 'image']);
    // console.log(vals);
    return new Draft(vals);
  }

  addParticipant(participant) {
    this.set({
      participants: [...this.store.participants, participant],
    });

    console.log('Added participant to Draft', this.store);

    this.syncUpdate();
  }

  removeParticipant(participant) {
    this.set({
      participants: [...this.store.participants].filter(p => p._id !== participant._id),
    });

    console.log('Removed participant from Draft', this.store);

    this.syncUpdate();
  }

  save() {
    if (!this._id) {
      console.log('Item has no id, not saving', this.toObject());
      return this;
    }

    return Promise.all([
      localforage.setItem(this._id, this.toObject()),
      localforage.setItem('currentDraft', this._id),
    ]);
  }

  serialize() {
    function checkVal(objVal, srcVal, key, obj, source) {

      console.log(objVal, srcVal);

      if (objVal === undefined || objVal === null) return null;

      if (objVal === srcVal) return objVal;

      if (_.isArray(srcVal)) {
        return [...objVal].map(o => checkVal(o, srcVal[0], key, obj, source));
      }

      if (key === 'image') {
        if (objVal && objVal.url) {
          return objVal.url;
        }

        if (_.isString(objVal)) {
          return objVal;
        }

        return obj;
      }

      if (srcVal === 'Template') {
        return objVal._id;
      }

      if (srcVal === 'User') {
        return objVal._id;
      }

      if (srcVal === 'DraftParent') {
        console.log('Got parents', obj, source);

        let prompt;

        if (obj.prompts && obj.prompts[objVal._id]) {
          prompt = obj.prompts[objVal._id];
        }

        return {
          parentId: objVal._id,
          parentType: objVal.parentType || objVal.type,
          prompt,
        };
      }
      return objVal;
    }

    const castedObject = _.omit(_.assignWith(this.toObject(), Draft.schema.Draft.fields, checkVal), ['_id', 'prompts']);

    const result = _.pickBy(castedObject, v => v);
    console.log('Serialize result', result);
    return result;
  }

  async syncCreate(props = {}) {
    this.set('createdBy', session.get('user'));


    return (({ models, mutate, client }) => {
      const { CreateOneDraftPayload, Draft, Template } = models;


      const record = _.omit(this.serialize(), ['_id']);

      if (props && props.isSeeded) {
        Object.assign(record, { isSeeded: true });
      }
      return new Promise((resolve, reject) => mutate(
        CreateOneDraftPayload('draftCreateOne', { record },
          Draft('record', Template('template').only(['_id'])).only(['_id'])
        ),
      ).generate((gqlStr) => {
        console.log(gqlStr);
        client.mutate({
          mutation: gql(gqlStr),
        }).then((result) => {
          console.dir(result);
          this.set(result.data.draftCreateOne.record);
          resolve(this.save());
        }).catch((err) => { console.error(err); });
      }));
    })(Draft.gql);
  }

  async syncUpdate() {
    return (({ models, mutate, client }) => {
      const { UpdateByIdDraftPayload, Draft } = models;

      const record = this.serialize();
      record._id = this._id;

      return new Promise((resolve, reject) => mutate(
        UpdateByIdDraftPayload('draftUpdateById', { record: _.pick(record, ['participants', '_id']) }, // Only update participants field for now
          Draft('record').only(['_id'])),
      ).generate((gqlStr) => {
        console.log('Update draft', gqlStr);
        client.mutate({
          mutation: gql(gqlStr),
        }).then((result) => {
          console.dir('Updated draft', result);
          this.set(result.data.draftUpdateById.record);
          resolve(this.save());
        }).catch(reject);
      }));
    })(Draft.gql);
  }

  syncImage(file) {
    // Revoke previous image preview before proceeding
    // if (this.get('image')) {
    //   if (this.get('image').preview) {
    //     URL.revokeObjectURL(this.get('image').preview);
    //   }
    // }

    const data = new FormData();
    data.append('image', file);

    fetch(`${config.backend.uri}/upload`, {
      method: 'POST',
      body: data,
    })
      .then(res => res.json())
      .then(res => this.set('image', { ...file, ...res.data.upload }));
  }

  fetchLocal() {
    return localforage.getItem('currentDraft')
      .then((currentDraft) => {
        if (!currentDraft) {
          return Promise.resolve();
        }
        return localforage.getItem(currentDraft);
      }).then((draft) => {
        if (!draft) {
          return Promise.resolve();
        }
        return this.set(draft);
      });
  }

  static isDraft(draft) {
    return draft.constructor.name === Draft.constructor.name;
  }

  static mock() {
    const parents = [...mockCardsList.genreMany.slice(0, 2), ...mockCardsList.storyMany.slice(0, 1)];
    const prompts = _.reduce(parents, (acc, curr) => _.assign(acc, { [curr._id]: faker.company.bs() }), {});
    return new Draft({
      _id: faker.random.uuid(),
      title: faker.company.catchPhrase(),
      blurb: faker.company.bs(),
      template: Template.mock(),
      prompts,
      parents,
      sessions: [],
      participants: _.range(3).map(v => User.mock()),
    });
  }
}

// Build Graphhql model
buildModels(Draft);



// WEBPACK FOOTER //
// ./src/models/Draft.js